(() => {
  'use strict';

  const carouselCleanups = new WeakMap();
  const marqueeCleanups = new WeakMap();
  const reviewCleanups = new WeakMap();
  const protectionCleanups = new WeakMap();

  function initializeCarousel(hero) {
    const slides = [...hero.querySelectorAll('[data-xm-slide]')];
    const dots = [...hero.querySelectorAll('[data-xm-dot]')];
    const track = hero.querySelector('.xm-hero__slides');
    const controls = hero.querySelector('.xm-hero__pagination');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = matchMedia('(max-width: 767px)');
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const interval = Math.max(3000, Number(hero.dataset.interval) || 3000);
    let index = 0;
    let timer;
    let visible = false;
    let hovered = false;
    let gesture = null;
    let slideAnimations = [];
    slides.forEach((slide) => slide.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; }));

    function schedule() {
      clearTimeout(timer);
      const focused = hero.querySelector(':focus-visible');
      const paused = hero.dataset.autoplay === 'false' || reducedMotion.matches || (focused && !focused.closest('[data-xm-marquee]'));
      track.setAttribute('aria-live', paused ? 'polite' : 'off');
      if (!paused && visible && mobile.matches && !document.hidden && !hovered && !gesture) {
        timer = setTimeout(() => show(index + 1), interval);
      }
    }

    function show(next) {
      const previous = index;
      const direction = next < index ? -1 : 1;
      slideAnimations.forEach((animation) => animation.cancel());
      slideAnimations = [];
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, position) => {
        const active = position === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        dots[position].setAttribute('aria-current', String(active));
      });
      if (index !== previous && !reducedMotion.matches) {
        const timing = { duration: 600, easing: 'cubic-bezier(.22,.61,.36,1)' };
        slideAnimations = [
          slides[previous].animate([
            { transform: 'translateX(0)', opacity: 1 },
            { transform: `translateX(${-direction * 100}%)`, opacity: 1 }
          ], timing),
          slides[index].animate([
            { transform: `translateX(${direction * 100}%)`, opacity: 1 },
            { transform: 'translateX(0)', opacity: 1 }
          ], timing)
        ];
      }
      schedule();
    }

    dots.forEach((dot, position) => dot.addEventListener('click', () => show(position), options));
    hero.addEventListener('keydown', (event) => {
      if (event.target.closest('[data-xm-marquee]')) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : index + (event.key === 'ArrowRight' ? 1 : -1);
      show(next);
      dots[index].focus({ preventScroll: true });
    }, options);
    hero.addEventListener('focusin', schedule, options);
    hero.addEventListener('focusout', schedule, options);
    hero.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      hovered = true;
      schedule();
    }, options);
    hero.addEventListener('pointerleave', () => {
      hovered = false;
      schedule();
    }, options);
    hero.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button !== 0 || event.target.closest('a, button, [data-xm-marquee]')) return;
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
      hero.setPointerCapture(event.pointerId);
      schedule();
    }, options);
    hero.addEventListener('pointerup', (event) => {
      if (!gesture || gesture.id !== event.pointerId) return;
      const x = event.clientX - gesture.x;
      const y = event.clientY - gesture.y;
      gesture = null;
      if (Math.abs(x) > 40 && Math.abs(x) > Math.abs(y) * 1.25) show(index + (x < 0 ? 1 : -1));
      else schedule();
    }, options);
    hero.addEventListener('pointercancel', () => {
      gesture = null;
      schedule();
    }, options);
    document.addEventListener('visibilitychange', schedule, options);
    mobile.addEventListener('change', schedule, options);
    reducedMotion.addEventListener('change', schedule, options);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      schedule();
    }, { threshold: 0 });
    observer.observe(hero);
    controls.hidden = false;
    schedule();
    return () => {
      clearTimeout(timer);
      slideAnimations.forEach((animation) => animation.cancel());
      observer.disconnect();
      controller.abort();
    };
  }

  function initializeMarquee(marquee) {
    const viewport = marquee.querySelector('.xm-media__viewport');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = matchMedia('(max-width: 767px)');
    const controller = new AbortController();
    const options = { signal: controller.signal };
    let visible = false;
    let hovered = false;

    function update() {
      const focused = viewport.matches(':focus-visible') || viewport.querySelector(':focus-visible');
      marquee.classList.toggle('is-keyboard-focused', Boolean(focused));
      marquee.classList.toggle('is-running', !focused && !hovered && visible && mobile.matches && !reducedMotion.matches && !document.hidden);
    }

    viewport.addEventListener('focusin', update, options);
    viewport.addEventListener('focusout', update, options);
    viewport.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      hovered = true;
      update();
    }, options);
    viewport.addEventListener('pointerleave', () => { hovered = false; update(); }, options);
    document.addEventListener('visibilitychange', update, options);
    reducedMotion.addEventListener('change', update, options);
    mobile.addEventListener('change', update, options);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      update();
    });
    observer.observe(marquee);
    marquee.classList.add('is-ready');
    update();
    return () => { observer.disconnect(); controller.abort(); };
  }

  function initializeReviewRail(rail, root) {
    const cards = [...rail.querySelectorAll('.xm-review')];
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = matchMedia('(max-width: 767px)');
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const direction = rail.classList.contains('xm-review-rail--1') ? -1 : 1;
    const copies = [];
    let groupWidth = 0;
    let position = 0;
    let lastWritten = 0;
    let lastTime = 0;
    let frame = 0;
    let resumeTimer = 0;
    let visible = false;
    let hovered = false;
    let pressed = false;
    let looping = false;

    if (cards.length > 1) {
      for (let copy = 0; copy < 2; copy += 1) {
        cards.forEach((card) => {
          const clone = card.cloneNode(true);
          clone.classList.add('xm-review--clone');
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-shopify-editor-block');
          clone.inert = true;
          rail.append(clone);
          copies.push(clone);
        });
      }
    }

    function stop() {
      cancelAnimationFrame(frame);
      frame = 0;
      lastTime = 0;
    }

    function write() {
      if (looping && groupWidth) position = groupWidth + ((position - groupWidth) % groupWidth + groupWidth) % groupWidth;
      rail.scrollLeft = position;
      lastWritten = rail.scrollLeft;
    }

    function tick(time) {
      if (lastTime) position += direction * Math.min(time - lastTime, 64) * 0.024;
      lastTime = time;
      write();
      frame = requestAnimationFrame(tick);
    }

    function schedule() {
      stop();
      if (looping && visible && !document.hidden && !hovered && !pressed && !resumeTimer && !rail.matches(':focus-visible')) {
        frame = requestAnimationFrame(tick);
      }
    }

    function measure() {
      stop();
      const oldWidth = groupWidth;
      const progress = oldWidth && looping ? ((position - oldWidth) % oldWidth + oldWidth) % oldWidth / oldWidth : null;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      groupWidth = cards.reduce((width, card) => width + card.getBoundingClientRect().width + gap, 0);
      looping = cards.length > 1 && mobile.matches && !reducedMotion.matches;
      rail.classList.toggle('is-looping', looping);
      const startOffset = Number(rail.dataset.startOffset) || 0;
      position = looping ? groupWidth + (progress === null ? root.clientWidth * startOffset / 402 : progress * groupWidth) : 0;
      write();
      schedule();
    }

    function pauseForInteraction() {
      stop();
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => { resumeTimer = 0; schedule(); }, 900);
    }

    rail.addEventListener('scroll', () => {
      if (Math.abs(rail.scrollLeft - lastWritten) < 1) return;
      position = rail.scrollLeft;
      pauseForInteraction();
      if (looping && (position < groupWidth || position >= groupWidth * 2)) write();
    }, { ...options, passive: true });
    rail.addEventListener('keydown', (event) => {
      if (event.target !== rail || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      pauseForInteraction();
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      position = rail.scrollLeft + ((cards[0]?.getBoundingClientRect().width || 185.5) + gap) * (event.key === 'ArrowRight' ? 1 : -1);
      write();
    }, options);
    rail.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      hovered = true;
      schedule();
    }, options);
    rail.addEventListener('pointerleave', () => { hovered = false; schedule(); }, options);
    rail.addEventListener('pointerdown', () => { pressed = true; schedule(); }, options);
    const release = () => {
      if (!pressed) return;
      pressed = false;
      position = rail.scrollLeft;
      pauseForInteraction();
    };
    document.addEventListener('pointerup', release, options);
    document.addEventListener('pointercancel', release, options);
    rail.addEventListener('wheel', pauseForInteraction, { ...options, passive: true });
    rail.addEventListener('focusin', schedule, options);
    rail.addEventListener('focusout', schedule, options);
    document.addEventListener('visibilitychange', schedule, options);
    mobile.addEventListener('change', measure, options);
    reducedMotion.addEventListener('change', measure, options);
    const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; schedule(); });
    observer.observe(rail);
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(rail);
    measure();

    return () => {
      stop();
      clearTimeout(resumeTimer);
      controller.abort();
      observer.disconnect();
      resizeObserver.disconnect();
      copies.forEach((copy) => copy.remove());
      rail.classList.remove('is-looping');
    };
  }

  function initializeProtection(optionsPanel) {
    const items = [...optionsPanel.querySelectorAll(':scope > details')];
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = matchMedia('(max-width: 767px)');
    const controller = new AbortController();
    const options = { signal: controller.signal };
    let timer;
    let visible = false;
    let hovered = false;

    function schedule() {
      clearTimeout(timer);
      if (items.length < 2 || !visible || !mobile.matches || reducedMotion.matches || document.hidden || hovered || optionsPanel.querySelector(':focus-visible')) return;
      timer = setTimeout(() => {
        const current = items.findIndex((item) => item.open);
        show((current + 1) % items.length);
      }, 2000);
    }

    function show(index, manual = false) {
      const item = items[index];
      items.forEach((other) => { if (other !== item) other.open = false; });
      item.open = true;
      // Only a user selection may reposition the page; autoplay never scrolls it.
      if (manual) {
        const top = item.getBoundingClientRect().top;
        if (top < 80) window.scrollBy({ top: top - 80, behavior: 'instant' });
      }
      schedule();
    }

    items.forEach((item, index) => {
      const summary = item.querySelector('summary');
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (item.open) schedule();
        else show(index, true);
      }, options);
      summary.addEventListener('keydown', (event) => {
        if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items[next].querySelector('summary').focus({ preventScroll: true });
        schedule();
      }, options);
    });
    optionsPanel.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      hovered = true;
      schedule();
    }, options);
    optionsPanel.addEventListener('pointerleave', () => { hovered = false; schedule(); }, options);
    optionsPanel.addEventListener('focusin', schedule, options);
    optionsPanel.addEventListener('focusout', schedule, options);
    document.addEventListener('visibilitychange', schedule, options);
    mobile.addEventListener('change', schedule, options);
    reducedMotion.addEventListener('change', schedule, options);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
      if (visible) optionsPanel.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; });
      schedule();
    }, { threshold: [0, 0.2] });
    observer.observe(optionsPanel);
    return () => { clearTimeout(timer); observer.disconnect(); controller.abort(); };
  }

  function initialize(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';

    const hero = root.querySelector('[data-xm-carousel]');
    if (hero) carouselCleanups.set(root, initializeCarousel(hero));
    const marquee = root.querySelector('[data-xm-marquee]');
    if (marquee) marqueeCleanups.set(root, initializeMarquee(marquee));

    const protection = root.querySelector('[data-xm-protection]');
    if (protection) protectionCleanups.set(root, initializeProtection(protection));

    const inlinePlay = root.querySelector('[data-xm-inline-play]');
    const inlineVideo = root.querySelector('.xm-demo__inline-video');
    if (inlinePlay && inlineVideo) {
      inlinePlay.hidden = false;
      inlineVideo.hidden = true;
      inlinePlay.addEventListener('click', () => {
        inlinePlay.hidden = true;
        inlineVideo.hidden = false;
        inlineVideo.focus({ preventScroll: true });
        inlineVideo.play().catch(() => {});
      });
    }

    const dialog = root.querySelector('.xm-video');
    const playButton = root.querySelector('[data-xm-play]');
    const video = dialog?.querySelector('video');

    if (dialog && video && playButton) {
      playButton.addEventListener('click', () => {
        dialog.showModal();
        video.play().catch(() => {});
      });
      root.querySelector('[data-xm-close]').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => {
        video.pause();
        playButton.focus({ preventScroll: true });
      });
    }

    reviewCleanups.set(root, [...root.querySelectorAll('[data-xm-rail]')].map((rail) => initializeReviewRail(rail, root)));
  }

  function start() {
    document.querySelectorAll('[data-xtrem-mobile-home]').forEach(initialize);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-xtrem-mobile-home]').forEach((root) => {
      carouselCleanups.get(root)?.();
      marqueeCleanups.get(root)?.();
      reviewCleanups.get(root)?.forEach((cleanup) => cleanup());
      protectionCleanups.get(root)?.();
    });
  });
})();
